/**
 * AddTextActionDialog - Dialog para adicionar/editar ação de texto
 * 
 * Permite inserir mensagem de texto com suporte a variáveis
 * como {{contact_name}}, {{contact_first_name}}, {{contact_phone}}
 *
 * RELEASE: 2026-08-06 v1.1.0 - Adicionada variável {{contact_first_name}} (primeiro nome).
 */


import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { MessageSquare } from 'lucide-react';

// =====================================================
// INTERFACES
// =====================================================

interface AddTextActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (message: string) => void;
  initialValue?: string;
}

// Variáveis disponíveis para routing (ações rápidas/macros)
const AVAILABLE_VARIABLES = [
  { name: 'contact_name', label: 'Nome do Contato' },
  { name: 'contact_first_name', label: 'Primeiro Nome do Contato' },
  { name: 'contact_phone', label: 'Telefone do Contato' },
];


// =====================================================
// COMPONENTE
// =====================================================

export function AddTextActionDialog({
  open,
  onOpenChange,
  onSave,
  initialValue,
}: AddTextActionDialogProps) {
  const [textContent, setTextContent] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset ao abrir dialog
  useEffect(() => {
    if (open) {
      setTextContent(initialValue || '');
    }
  }, [open, initialValue]);

  /**
   * Handler para inserir variável no cursor
   */
  const handleInsertVariable = (varName: string) => {
    const variable = `{{${varName}}}`;
    const textarea = textareaRef.current;
    
    if (!textarea) {
      setTextContent(prev => prev + variable);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = textContent.substring(0, start);
    const after = textContent.substring(end);
    
    const newContent = before + variable + after;
    setTextContent(newContent);
    
    // Reposicionar cursor após a variável inserida
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + variable.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  // Handler de salvar
  const handleSave = () => {
    if (textContent.trim()) {
      onSave(textContent.trim());
    }
  };

  // Validação
  const isValid = textContent.trim().length > 0;

  /**
   * Gera preview substituindo variáveis com valores de exemplo
   */
  const getPreviewText = () => {
    let preview = textContent;
    // Primeiro nome é substituído antes do nome completo para evitar ambiguidade de token
    preview = preview.replace(/\{\{contact_first_name\}\}/gi, 'João');
    preview = preview.replace(/\{\{contact_name\}\}/gi, 'João Silva');
    preview = preview.replace(/\{\{contact_phone\}\}/gi, '5562999999999');
    return preview;
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            {initialValue ? 'Editar Mensagem de Texto' : 'Adicionar Mensagem de Texto'}
          </DialogTitle>
          <DialogDescription>
            Digite a mensagem que será enviada automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Seletor de variáveis */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Inserir variável:</Label>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_VARIABLES.map((v) => (
                <Badge
                  key={v.name}
                  variant="secondary"
                  className="cursor-pointer hover:bg-secondary/80"
                  onClick={() => handleInsertVariable(v.name)}
                >
                  {`{{${v.name}}}`}
                </Badge>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="text-content">Mensagem *</Label>
            <Textarea
              ref={textareaRef}
              id="text-content"
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="Olá {{contact_name}}! Como posso ajudar?"
              rows={5}
              className="resize-none"
            />
          </div>

          {/* Pré-visualização */}
          {textContent && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Pré-visualização:</Label>
              <div className="p-3 bg-muted rounded-lg text-sm whitespace-pre-wrap max-h-[120px] overflow-y-auto">
                {getPreviewText()}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={!isValid}>
            {initialValue ? 'Salvar' : 'Adicionar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
